import { isCardElementEditable } from '../AccountCard/helpers';
import { hexToRGBA } from '../../../../util/hex-to-rgba';
import { CARD_ELEMENT_TYPE, PHOTO_TYPE } from '../../constants';
import { isElementCardBackground } from '../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';

/**
 * Determines whether the team-photo selection flow applies to a given
 * photo CardElement, mirroring the check in CardElementPhoto. When true,
 * the element should remain interactable even if not normally editable so
 * the user can switch between team photos.
 *
 * @param {Object} element The element UI state.
 * @param {Array} [teamPhotos] The team photos available for the card.
 * @returns {boolean}
 */
export const shouldUseTeamPhotosSelection = (element, teamPhotos = []) => {
  if (element?.cardElement?.type !== CARD_ELEMENT_TYPE.PHOTO) {
    return false;
  }

  const photoId = element?.cardElementConfiguration?.photo?.id;

  if (!photoId || !teamPhotos?.length) {
    return false;
  }

  return (
    teamPhotos.some(tp => tp?.photo?.id === photoId) &&
    !isCardElementEditable(element)
  );
};

/**
 * Determines whether the `alwaysAllowPhotoChange` override (e.g. admins,
 * PROs remaking an order) applies to a given element. Every configured
 * photo slot is unlocked, no matter how it was filled: auto-populated
 * slots such as team photos are written as non-editable and may hold a
 * Photo, a plain photo URL or an AssetImage.
 *
 * Only the decorative layers stay locked: the Card's backdrop, which
 * belongs to the backdrop picker, and the foreground art and logos which
 * sit on top of the photos and would swallow clicks meant for them.
 *
 * @param {Object} element The element UI state.
 * @returns {boolean}
 */
export const canAlwaysAllowPhotoChange = element => {
  const { cardElement, cardElementConfiguration } = element ?? {};

  if (cardElement?.type !== CARD_ELEMENT_TYPE.PHOTO) {
    return false;
  }

  const photoType = cardElementConfiguration?.photo_type;

  if (
    photoType === PHOTO_TYPE.BACKGROUND ||
    photoType === PHOTO_TYPE.FOREGROUND ||
    photoType === PHOTO_TYPE.LOGO
  ) {
    return false;
  }

  const showsPhoto = !!(
    cardElementConfiguration?.photo?.id || cardElementConfiguration?.photo_url
  );

  // A slot showing a Photo is never the Card's backdrop, which is always
  // built out of an AssetImage. Auto-populated slots are locked once filled,
  // which is enough for the `isElementCardBackground` heuristic (full-frame,
  // locked, bottom layer) to mistake a full-bleed team photo for one, so for
  // those only the explicit BACKGROUND marker above counts.
  if (!showsPhoto && isElementCardBackground(element)) {
    return false;
  }

  // Anything left showing an image is a configured photo slot, AssetImage
  // backed ones included. Empty slots are only unlocked when they already
  // are editable.
  return (
    showsPhoto ||
    !!cardElementConfiguration?.asset_image?.id ||
    isCardElementEditable(element)
  );
};

/**
 * Converts a float into a percentage value for usage in CSS.
 *
 * @param {Number} float The float number.
 * @returns {string}
 */
const percentage = float => {
  if (!float) {
    return '0';
  }

  return `${float * 100}%`;
};

/**
 * Constructs the styles of a CardElement's frame.
 *
 * @param {Object} element The element UI state.
 * @param {Object} element.cardElement The CardElement.
 * @param {Object} element.cardElementConfiguration The CardElementConfiguration.
 * @param {Array} teamPhotos The team photos array provided by upper level components
 * @param {Boolean} alwaysAllowPhotoChange whether or not to ignore all the rest and always allow changing photos
 * @returns {Object}
 */
export const getCardElementFrameStyle = (
  element,
  teamPhotos = [],
  alwaysAllowPhotoChange = false,
) => {
  const { cardElement } = element;
  const isEditable = isCardElementEditable(element);
  // Allow click-through when the photo element is using team-photo selection,
  // even if not normally editable, so the user can switch between team photos.
  // Also allow it when `alwaysAllowPhotoChange` is set (e.g. admins) so a photo
  // slot can always be interacted with to change its photo. Decorative
  // elements (backdrops, foregrounds, logos, card art) stay locked.
  const isInteractable =
    isEditable ||
    shouldUseTeamPhotosSelection(element, teamPhotos) ||
    (alwaysAllowPhotoChange && canAlwaysAllowPhotoChange(element));

  const styles = {
    zIndex: cardElement.z_index,
    width: percentage(cardElement.frame.width),
    height: percentage(cardElement.frame.height),
    left: percentage(cardElement.frame.x),
    top: percentage(cardElement.frame.y),
    transform: `rotate(${cardElement.frame.rotation}rad)`,
    pointerEvents: isInteractable ? 'auto' : 'none',
  };

  return styles;
};

/**
 * Constructs the styles of a CardElement's frame container.
 *
 * @param {Object} element The element UI state.
 * @param {Object} element.cardElement The CardElement.
 * @param {Object} element.cardElementConfiguration The CardElementConfiguration.
 * @param {Object} [elementBounds] The CardSide's size (width/height).
 * @param {Number} [elementBounds.width] The Element's width in px.
 * @param {Number} [elementBounds.height] The Element's width in px.
 * @returns {Object}
 */
export const getCardElementFrameContainerStyle = (element, elementBounds) => {
  const { cardElement, cardElementConfiguration = {} } = element;
  const { border } = cardElementConfiguration ?? {};
  const cornerRadius = cardElementConfiguration?.corner_radius ?? 0;
  const minPixelDimension = Math.min(elementBounds.width, elementBounds.height);

  const styles = {
    borderRadius: `${Math.ceil((minPixelDimension * cornerRadius) / 2)}px`,
  };

  if (border?.width && elementBounds?.width) {
    styles.borderStyle = border.type.toLowerCase();
    styles.borderColor = hexToRGBA(border.color, border.opacity);

    if (border.width) {
      styles.boxSizing = 'border-box';
      // Border width is based on the width of the element
      // Dive by 2, since the max border is half of the minimum size
      styles.borderWidth = `${Math.ceil(
        (minPixelDimension * border.width) / 2,
      )}px`;
    }
  }

  // CardElementType specific styles

  if (cardElement.type === CARD_ELEMENT_TYPE.TEXT) {
    if (cardElementConfiguration.text_shadow) {
      const { text_shadow: shadow } = cardElementConfiguration;

      styles.textShadow = `${shadow.offset_x}px ${shadow.offset_y}px ${
        shadow.blur_radius
      }px ${hexToRGBA(shadow.color, shadow.opacity)}`;
    }

    if (typeof cardElementConfiguration.opacity !== 'undefined') {
      styles.opacity = cardElementConfiguration.opacity;
    }
  }

  if (cardElement.type === CARD_ELEMENT_TYPE.RECTANGLE) {
    if (cardElementConfiguration.color) {
      styles.color = cardElementConfiguration.color;
      styles.backgroundColor = cardElementConfiguration.color;
    }
  }

  if (cardElement.type === CARD_ELEMENT_TYPE.PHOTO) {
    if (cardElementConfiguration.background_color) {
      styles.backgroundColor = cardElementConfiguration.background_color;
    }

    if (
      cardElementConfiguration.asset_image?.url &&
      !cardElementConfiguration.cropbox
    ) {
      styles.backgroundImage = `url(${cardElementConfiguration.asset_image.url})`;
      styles.backgroundSize = 'cover';
      styles.backgroundRepeat = 'no-repeat';
      styles.backgroundPosition = 'center center';
    }

    if (cardElementConfiguration.photo_type === PHOTO_TYPE.LOGO) {
      styles.backgroundSize = 'contain';
    }

    if (cardElementConfiguration.box_shadow) {
      const { box_shadow: shadow } = cardElementConfiguration;

      styles.boxShadow = `${hexToRGBA(shadow.color, shadow.opacity)} ${
        shadow.offset_x
      }px ${shadow.offset_y}px ${shadow.blur_radius}px`;
    }

    if (typeof cardElementConfiguration.opacity !== 'undefined') {
      styles.opacity = cardElementConfiguration.opacity;
    }
  }

  return styles;
};
